import {
  RiArrowLeftLine,
  RiCheckLine,
  RiDeleteBinLine,
  RiEditLine,
  RiPauseLine,
} from 'react-icons/ri';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useMemo, useState, type JSX } from 'react';
import { ToastContainer } from '@/components/features';
import { ConfirmModal } from '@/components/ui';
import { effectiveTierValueType } from '@/components/ui/OfferProductPreview';
import { mergePartialOfferPatch } from '@/pages/BrandOffersPage/brandOfferPayload.utils';
import {
  deleteBrandOffer,
  fetchBrandOffer,
  fetchBrandsForOffers,
  patchBrandOffer,
} from '@/services/slaydAPI/brandOffers';
import useBrandOfferStore from '@/stores/brandOfferStore';
import useToastStore from '@/stores/ToastStore';
import type { BrandOffer, OfferType } from '@/types/BrandOffer.types';
import BrandOfferForm from '../BrandOffersPage/BrandOfferForm';
import { normalizeBrandOfferRecord } from '../BrandOffersPage/BrandOfferForm/BrandOfferForm.utils';
import {
  BRAND_OFFERS_PAGE_COPY,
  OFFER_TYPE_COLORS,
  OFFER_TYPE_LABELS,
} from '../BrandOffersPage/BrandOffersPage.constants';
import styles from './BrandOfferDetailPage.module.css';

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <span className={styles.fieldValue}>{children}</span>
    </div>
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function BrandOfferDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { brands, setBrands, setBrandsLoading } = useBrandOfferStore();

  const { showToast } = useToastStore();

  const [offer, setOffer] = useState<BrandOffer | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const brandMap = useMemo(
    () => new Map(brands.map((b) => [b.id, b.name])),
    [brands]
  );

  useEffect(() => {
    const numId = Number(id);
    if (!Number.isFinite(numId)) {
      setFetchError('Invalid offer id.');
      return;
    }
    setIsFetching(true);
    setFetchError(null);
    fetchBrandOffer(numId)
      .then((o) => {
        setOffer(normalizeBrandOfferRecord(o));
      })
      .catch(() =>
        setFetchError('Could not load this offer. It may have been deleted.')
      )
      .finally(() => setIsFetching(false));
  }, [id]);

  useEffect(() => {
    if (brands.length > 0) return;
    setBrandsLoading(true);
    fetchBrandsForOffers()
      .then((data) => setBrands(data))
      .catch((error) => {
        console.error('Error fetching brands for offers:', error);
        showToast(BRAND_OFFERS_PAGE_COPY.brandsLoadError);
      })
      .finally(() => setBrandsLoading(false));
  }, [brands.length, setBrands, setBrandsLoading, showToast]);

  const handleToggleActive = async (): Promise<void> => {
    if (!offer) return;
    setIsToggling(true);
    setActionError(null);
    try {
      const updated = await patchBrandOffer(
        offer.id,
        mergePartialOfferPatch(offer, {
          isActive: !offer.isActive,
        })
      );
      const normalized = normalizeBrandOfferRecord(updated);
      setOffer(normalized);
      showToast(
        normalized.isActive ? 'Offer is live on the app.' : 'Offer paused.'
      );
    } catch {
      const msg = 'Could not update live state. Try again.';
      setActionError(msg);
      showToast(msg);
    } finally {
      setIsToggling(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!offer) return;
    setIsDeleting(true);
    try {
      await deleteBrandOffer(offer.id);
      showToast('Offer removed.');
      navigate('/brand-offers');
    } catch {
      const msg = 'Delete failed. The offer may still be referenced.';
      setActionError(msg);
      showToast(msg);
      setIsDeleting(false);
      setDeleteConfirm(false);
    }
  };

  const handleFormSaved = (saved: BrandOffer): void => {
    setOffer(normalizeBrandOfferRecord(saved));
    setIsFormOpen(false);
  };

  if (isFetching) {
    return (
      <div className={styles.page}>
        <div className={styles.centeredState}>
          <div className={styles.spinner} />
          <p className={styles.stateText}>Loading offer…</p>
        </div>
      </div>
    );
  }

  if (fetchError || !offer) {
    return (
      <div className={styles.page}>
        <div className={styles.centeredState}>
          <p className={styles.errorText}>{fetchError ?? 'Offer not found.'}</p>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => navigate('/brand-offers')}
          >
            <RiArrowLeftLine size={16} /> Back to offers
          </button>
        </div>
      </div>
    );
  }

  const typeColor = OFFER_TYPE_COLORS[offer.offerType as OfferType];
  const brandName =
    offer.brand === null
      ? '—'
      : (brandMap.get(offer.brand) ?? `Brand #${offer.brand}`);

  const dc = offer.discountConfig;

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        <div className={styles.nav}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => navigate('/brand-offers')}
          >
            <RiArrowLeftLine size={16} />
            All offers
          </button>
        </div>

        <header className={styles.header}>
          <div className={styles.headerMeta}>
            <span className={styles.idLabel}>#{offer.id}</span>
            <span
              className={styles.typeBadge}
              style={{
                backgroundColor: typeColor + '22',
                color: typeColor,
              }}
            >
              {OFFER_TYPE_LABELS[offer.offerType as OfferType]}
            </span>
            <span
              className={
                offer.isActive
                  ? `${styles.statusPill} ${styles.statusOn}`
                  : styles.statusPill
              }
            >
              {offer.isActive ? 'Live' : 'Paused'}
            </span>
          </div>
          <h1 className={styles.title}>{offer.name}</h1>
          <div className={styles.headerActions}>
            <button
              type="button"
              className={
                offer.isActive ? styles.deactivateBtn : styles.activateBtn
              }
              disabled={isToggling}
              onClick={handleToggleActive}
            >
              {offer.isActive ? (
                <>
                  <RiPauseLine size={15} /> Pause
                </>
              ) : (
                <>
                  <RiCheckLine size={15} /> Turn on
                </>
              )}
            </button>
            <button
              type="button"
              className={styles.editBtn}
              onClick={() => setIsFormOpen(true)}
            >
              <RiEditLine size={15} /> Edit
            </button>
            <button
              type="button"
              className={styles.deleteBtn}
              onClick={() => setDeleteConfirm(true)}
            >
              <RiDeleteBinLine size={15} /> Delete
            </button>
          </div>
        </header>

        {actionError && (
          <div className={styles.errorBanner}>
            <p>{actionError}</p>
          </div>
        )}

        <div className={styles.body}>
          <section className={styles.section}>
            <p className={styles.sectionTitle}>Overview</p>
            <div className={styles.fieldGrid}>
              <Field label="Offer ID">{offer.id}</Field>
              <Field label="Offer name">{offer.name}</Field>
              <Field label="Type">
                <span
                  className={styles.typeBadge}
                  style={{
                    backgroundColor: typeColor + '22',
                    color: typeColor,
                  }}
                >
                  {OFFER_TYPE_LABELS[offer.offerType as OfferType]}
                </span>
              </Field>
              <Field label="Brand">{brandName}</Field>
              <Field label="Priority (rank)">{offer.priority}</Field>
              <Field label="Live on app">
                <span
                  className={
                    offer.isActive
                      ? `${styles.statusPill} ${styles.statusOn}`
                      : styles.statusPill
                  }
                >
                  {offer.isActive ? 'Live' : 'Paused'}
                </span>
              </Field>
              <Field label="On sale">
                <span
                  className={
                    offer.isSale
                      ? `${styles.statusPill} ${styles.statusOn}`
                      : styles.statusPill
                  }
                >
                  {offer.isSale ? 'Yes' : 'No'}
                </span>
              </Field>
              <Field label="Show timer">
                <span
                  className={
                    offer.showTimer
                      ? `${styles.statusPill} ${styles.statusOn}`
                      : styles.statusPill
                  }
                >
                  {offer.showTimer ? 'Yes' : 'No'}
                </span>
              </Field>
              <Field label="Starts at">
                {offer.startAt ? (
                  <span>{formatDate(offer.startAt)}</span>
                ) : (
                  <span className={styles.muted}>Not scheduled</span>
                )}
              </Field>
              <Field label="Ends at">
                {offer.endsAt ? (
                  <span
                    className={
                      new Date(offer.endsAt) < new Date()
                        ? styles.expiredDate
                        : undefined
                    }
                  >
                    {formatDate(offer.endsAt)}
                    {new Date(offer.endsAt) < new Date() && ' · Expired'}
                  </span>
                ) : (
                  <span className={styles.muted}>No end date</span>
                )}
              </Field>
              <Field label="Created at">{formatDate(offer.createdAt)}</Field>
              <Field label="Updated at">{formatDate(offer.updatedAt)}</Field>
            </div>
          </section>

          {dc && (
            <section className={styles.section}>
              <p className={styles.sectionTitle}>Discount config</p>
              <div className={styles.fieldGrid}>
                <Field label="Discount type">{dc.type}</Field>
                {dc.value !== undefined &&
                  dc.value !== null &&
                  (dc.type === 'FLAT' ? (
                    <Field label="Value">₹{String(dc.value)}</Field>
                  ) : dc.type === 'PERCENT' ? (
                    <Field label="Value">{String(dc.value)}%</Field>
                  ) : (
                    <Field label="Value">{String(dc.value)}</Field>
                  ))}
                {dc.offerText && (
                  <Field label="Offer text">{dc.offerText}</Field>
                )}
              </div>
              {dc.rules && dc.rules.length > 0 && (
                <div className={styles.tiersWrap}>
                  <p className={styles.tiersLabel}>Tier rules</p>
                  <div className={styles.tiersTable}>
                    <div className={styles.tiersHead}>
                      <span>Min</span>
                      <span>Max</span>
                      <span>Type</span>
                      <span>Amount</span>
                    </div>
                    {dc.rules.map((rule) => {
                      const k = effectiveTierValueType(rule, offer.offerType);
                      return (
                        <div
                          key={`${rule.min}-${rule.max}`}
                          className={styles.tiersRow}
                        >
                          <span>{rule.min}</span>
                          <span>{rule.max ?? '∞'}</span>
                          <span>
                            {k === 'PERCENT' ? 'Percentage' : 'Flat ₹'}
                          </span>
                          <span>
                            {k === 'PERCENT'
                              ? `${rule.value}%`
                              : `₹${rule.value}`}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </section>
          )}
        </div>
      </div>

      {deleteConfirm && (
        <ConfirmModal
          eyebrow="Permanent removal"
          body={
            <>
              Remove offer{' '}
              <strong>
                #{offer.id} — {offer.name}
              </strong>
              ? Shoppers will stop seeing it immediately.
            </>
          }
          cancelLabel="Keep it"
          confirmLabel={isDeleting ? 'Removing…' : 'Remove offer'}
          isLoading={isDeleting}
          onCancel={() => setDeleteConfirm(false)}
          onConfirm={handleDelete}
        />
      )}

      {isFormOpen && (
        <BrandOfferForm
          offer={offer}
          onClose={() => setIsFormOpen(false)}
          onSaved={handleFormSaved}
        />
      )}
      <ToastContainer />
    </div>
  );
}
